/**
 * Utility functions for coordinate conversion between timeline and zoom keyframe systems.
 *
 * Coordinate Systems:
 * - Timeline: Uses seconds for absolute positions (item.start, item.end)
 * - Zoom Keyframes: Uses frames relative to clip start (keyframe.startFrame, keyframe.endFrame)
 * - UI Percentage: Used for CSS positioning (0-100%)
 */

/**
 * Converts zoom keyframe frame position to percentage within timeline item.
 * @param frame Frame number relative to overlay start (0 = clip start)
 * @param clipDurationFrames Total frames in the clip
 * @returns Percentage (0-100)
 */
export function frameToItemPercent(frame: number, clipDurationFrames: number): number {
  if (clipDurationFrames <= 0) return 0;
  return (frame / clipDurationFrames) * 100;
}

/**
 * Converts timeline item percentage to keyframe frame position.
 * @param percent Percentage within clip (0-100)
 * @param clipDurationFrames Total frames in the clip
 * @returns Frame number relative to overlay start
 */
export function itemPercentToFrame(percent: number, clipDurationFrames: number): number {
  return Math.round((percent / 100) * clipDurationFrames);
}

/**
 * Converts absolute timeline seconds to keyframe frame (relative to clip start).
 * @param timelineSeconds Absolute position in timeline (seconds)
 * @param clipStartSeconds Where clip starts in timeline (seconds)
 * @param fps Frames per second
 * @returns Frame number relative to overlay start
 */
export function timelineSecondsToClipFrame(
  timelineSeconds: number,
  clipStartSeconds: number,
  fps: number
): number {
  return Math.round((timelineSeconds - clipStartSeconds) * fps);
}

/**
 * Converts keyframe frame to absolute timeline seconds.
 * @param frame Frame relative to clip start
 * @param clipStartSeconds Where clip starts in timeline
 * @param fps Frames per second
 * @returns Absolute timeline position in seconds
 */
export function clipFrameToTimelineSeconds(
  frame: number,
  clipStartSeconds: number,
  fps: number
): number {
  return clipStartSeconds + frame / fps;
}

/**
 * Converts pixel delta to frame delta based on container width and clip duration.
 * @param pixelDelta Change in pixels
 * @param containerWidth Width of the timeline item container in pixels
 * @param clipDurationFrames Total frames in the clip
 * @returns Frame delta
 */
export function pixelDeltaToFrameDelta(
  pixelDelta: number,
  containerWidth: number,
  clipDurationFrames: number
): number {
  if (containerWidth <= 0) return 0;
  const percentDelta = (pixelDelta / containerWidth) * 100;
  return Math.round((percentDelta / 100) * clipDurationFrames);
}

/**
 * Clamps a frame value to valid boundaries.
 * @param frame Frame to clamp
 * @param minFrame Minimum allowed frame (usually 0)
 * @param maxFrame Maximum allowed frame (usually clip duration)
 * @returns Clamped frame value
 */
export function clampFrame(frame: number, minFrame: number, maxFrame: number): number {
  return Math.max(minFrame, Math.min(maxFrame, frame));
}

/**
 * Checks if a new keyframe position would overlap with existing keyframes.
 * @param newStartFrame Start frame of new/moved keyframe
 * @param newEndFrame End frame of new/moved keyframe
 * @param existingKeyframes Array of existing keyframes
 * @param excludeId Optional keyframe ID to exclude from check (for moving existing keyframe)
 * @returns true if there would be an overlap
 */
export function wouldOverlap(
  newStartFrame: number,
  newEndFrame: number,
  existingKeyframes: { id: string; startFrame: number; endFrame: number }[],
  excludeId?: string
): boolean {
  return existingKeyframes.some((kf) => {
    if (excludeId && kf.id === excludeId) return false;
    // Two ranges overlap if neither is completely before or after the other
    return !(newEndFrame <= kf.startFrame || newStartFrame >= kf.endFrame);
  });
}

/**
 * Gets the scale preset key from a scale value.
 * @param scale Zoom scale value
 * @returns Preset key or 'custom'
 */
export function getScalePreset(scale: number): string {
  if (Math.abs(scale - 1) < 0.01) return "default";
  if (Math.abs(scale - 1.5) < 0.01) return "shallow";
  if (Math.abs(scale - 2) < 0.01) return "moderate";
  if (Math.abs(scale - 3) < 0.01) return "maximum";
  return "custom";
}

/**
 * Gets the color class for a scale value.
 */
export function getScaleColorClass(scale: number): string {
  const preset = getScalePreset(scale);
  const colors: Record<string, string> = {
    default: "bg-gray-400",
    shallow: "bg-blue-500",
    moderate: "bg-purple-500",
    maximum: "bg-pink-500",
    custom: "bg-orange-500",
  };
  return colors[preset] || colors.custom;
}

/**
 * Formats frame count to time string.
 * @param frames Number of frames
 * @param fps Frames per second
 * @returns Formatted time string (e.g., "1.5s")
 */
export function formatFramesToTime(frames: number, fps: number): string {
  const seconds = frames / fps;
  return `${seconds.toFixed(1)}s`;
}
